import { ref, computed } from 'vue';
import notify from 'devextreme/ui/notify';

export function usePrintReport() {
    const isPrinting = ref(false);
    const pdfBlobUrl = ref<string | null>(null);
    const pdfFileName = ref('');
    const isPdfPreviewVisible = ref(false);

    // Sursă pentru <iframe> — ascunde bara de instrumente nativă a viewer-ului PDF din browser
    // (pagini, zoom, descărcare, print), ca să rămână vizibil doar documentul; butoanele proprii
    // Descarcă/Închide din popup rămân singura interacțiune. Fragmentul #toolbar=0 nu afectează
    // conținutul blob-ului, deci descărcarea (care folosește pdfBlobUrl, nu asta) nu e afectată.
    const pdfPreviewSrc = computed(() => pdfBlobUrl.value ? `${pdfBlobUrl.value}#toolbar=0&navpanes=0&scrollbar=0` : null);

    /**
     * fileName opțional: numele propus la Salvare/Download din viewer-ul PDF încorporat al browserului
     * (best-effort — Chrome/Edge îl respectă, Firefox/Safari pot ignora și propune un nume generic).
     * PDF-ul se deschide oricum în tab nou pentru previzualizare, indiferent de fileName.
     */
    async function printReport(url: string, token: string, fileName?: string): Promise<boolean> {
        if (isPrinting.value) return false;
        isPrinting.value = true;
        try {
            const resp = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
            if (resp.ok && resp.headers.get('content-type')?.includes('application/pdf')) {
                const blob = await resp.blob();
                const namedBlob = fileName ? new File([blob], fileName, { type: 'application/pdf' }) : blob;
                const blobUrl = URL.createObjectURL(namedBlob);
                const win = window.open(blobUrl, '_blank');
                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                if (!win) notify('Permiteți pop-up-uri pentru a vizualiza documentul.', 'warning', 4000);
                return true;
            }
            const msg = await resp.text().catch(() => '');
            notify(msg || `Eroare ${resp.status} la generarea raportului.`, 'error', 5000);
            return false;
        } catch {
            notify('Eroare la conectarea cu serverul de rapoarte.', 'error', 4000);
            return false;
        } finally {
            isPrinting.value = false;
        }
    }

    /** Descarcă direct PDF-ul cu fileName, garantat pe toate browserele (nu deschide preview). */
    async function downloadReport(url: string, token: string, fileName: string): Promise<boolean> {
        if (isPrinting.value) return false;
        isPrinting.value = true;
        try {
            const resp = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
            if (resp.ok && resp.headers.get('content-type')?.includes('application/pdf')) {
                const blob = await resp.blob();
                const blobUrl = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
                return true;
            }
            const msg = await resp.text().catch(() => '');
            notify(msg || `Eroare ${resp.status} la generarea raportului.`, 'error', 5000);
            return false;
        } catch {
            notify('Eroare la conectarea cu serverul de rapoarte.', 'error', 4000);
            return false;
        } finally {
            isPrinting.value = false;
        }
    }

    /** Deschide PDF-ul într-un popup in-page (iframe pe blob URL), cu numele corect — nu în tab nou. */
    async function previewReport(url: string, token: string, fileName: string): Promise<boolean> {
        if (isPrinting.value) return false;
        isPrinting.value = true;
        try {
            const resp = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
            if (resp.ok && resp.headers.get('content-type')?.includes('application/pdf')) {
                const blob = await resp.blob();
                closePdfPreview();
                pdfFileName.value = fileName;
                pdfBlobUrl.value = URL.createObjectURL(blob);
                isPdfPreviewVisible.value = true;
                return true;
            }
            const msg = await resp.text().catch(() => '');
            notify(msg || `Eroare ${resp.status} la generarea raportului.`, 'error', 5000);
            return false;
        } catch {
            notify('Eroare la conectarea cu serverul de rapoarte.', 'error', 4000);
            return false;
        } finally {
            isPrinting.value = false;
        }
    }

    function downloadPreviewReport(): void {
        if (!pdfBlobUrl.value) return;
        const a = document.createElement('a');
        a.href = pdfBlobUrl.value;
        a.download = pdfFileName.value || 'Raport.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
    }

    // Tipărire directă din popup-ul de previzualizare — deschide dialogul nativ de print al
    // browserului pentru PDF-ul deja încărcat în <iframe> (fără să treacă prin descărcare).
    // iframeEl vine din template ref pe elementul <iframe :src="pdfPreviewSrc">.
    function printPreviewReport(iframeEl: HTMLIFrameElement | null | undefined): void {
        if (!iframeEl?.contentWindow) return;
        iframeEl.contentWindow.focus();
        iframeEl.contentWindow.print();
    }

    function closePdfPreview(): void {
        isPdfPreviewVisible.value = false;
        if (pdfBlobUrl.value) {
            URL.revokeObjectURL(pdfBlobUrl.value);
            pdfBlobUrl.value = null;
        }
    }

    return {
        isPrinting, printReport, downloadReport,
        pdfBlobUrl, pdfPreviewSrc, pdfFileName, isPdfPreviewVisible,
        previewReport, downloadPreviewReport, printPreviewReport, closePdfPreview,
    };
}
